<script lang="ts">
	//Hooks
	import { onMount, onDestroy, tick } from 'svelte';
	//Formatters
	import { format } from 'svelte-i18n';
	//Helpers
	import { dateFormat } from '../../../../helpers/date_helper';
	import { get_local_ip, real_id } from '../../../../helpers/interface_helpers';
	//Stores
		import { getDeviceStatus, getDeviceSettings, readDeviceStatus } from 'src/stores/device-runtime/status-selectors';
	//Design system components
	import { Icon, toast } from '@Shelly/shelly-design-system';
	//Components
	import type { ScripsHelper } from '../scripts_helpers';

	$: statusStore = getDeviceStatus(scripsHelper.deviceID);
	export let scripsHelper: ScripsHelper, scriptID: number, running: boolean;

	const worksOnlyOnLocal = false;
	$: localIP = worksOnlyOnLocal
		? get_local_ip(scripsHelper.deviceID)
		: $statusStore?.wifi?.sta_ip;
	$: settingsStore = getDeviceSettings(scripsHelper.deviceID);
	$: logEnabled =
		localIP &&
		/^(http|file):/.test(window.location.protocol) &&
		$settingsStore?.sys?.debug?.websocket?.enable;

	let debugSocket: WebSocket = null,
		wsURL = localIP ? localIP.replace('http://', '') : '',
		consoleHeight = 105,
		fullLog = false,
		wsRunning = false,
		loadingSocket = false,
		waitForEvalRequest = false,
		terminalPlaceholder = "Test your code here e.g. print('Hello World')",
		placeholder = terminalPlaceholder,
		terminalValue = '',
		consoleBox: HTMLElement,
		log: {
			message: string;
			time: string;
			color: string;
			level: number;
		}[] = [];

	const startDebugLog = () => {
		loadingSocket = true;
		try {
			debugSocket = new WebSocket(`ws://${wsURL}/debug/log`);
			debugSocket.onopen = async (e) => {
				log.push({
					message: `WebSocket is open to ${wsURL}`,
					time: '',
					level: -1,
					color: 'success',
				});
				wsRunning = true;
				loadingSocket = false;
				updateConsoleLog();
			};
			debugSocket.onclose = async (e) => {
				log.push({
					message: `WebSocket is closed to ${wsURL} ${e.code != 1005 ? `Error code: ${e.code}` : ''}`,
					time: dateFormat(new Date(), 'H:i:s'),
					level: -1,
					color: 'danger',
				});
				wsRunning = false;
				loadingSocket = false;
				debugSocket = null;
				updateConsoleLog();
			};
			debugSocket.onmessage = async (msg) => {
				const data = JSON.parse(msg.data);
				let level = data.level;
				if (level == 2 && data?.fd === scriptID + 100) {
					level = -1;
				}
				log.push({
					level,
					color: 'color-1',
					message: data.data,
					time: dateFormat(data.ts * 1000, 'H:i:s'),
				});
				updateConsoleLog();
			};
		} catch (err) {
			log.push({
				message: err,
				time: dateFormat(new Date(), 'H:i:s'),
				level: -1,
				color: 'danger',
			});
			wsRunning = false;
			loadingSocket = false;
			debugSocket = null;
			updateConsoleLog();
		}
	};

	const updateConsoleLog = async () => {
		log = log;
		if (consoleBox) {
			await tick();
			if (consoleBox?.scrollTop != null) {
				consoleBox.scrollTop = consoleBox?.scrollHeight;
			}
		}
	};
	const resizeWrapper = (resizer: HTMLElement) => {
		let offsetY = 0,
			consoleOffsetHeight = 0;
		const startDrag = (e) => {
				if (
					(e.type == 'touchstart' && e.touches.length != 1) ||
					(e.type == 'mousedown' && e.button > 0)
				) {
					return;
				}
				consoleOffsetHeight = consoleHeight;
				if (e.type == 'touchstart') {
					offsetY = e.touches[0].clientY;
					document.addEventListener('touchend', stopDrag, { once: true });
					document.addEventListener('touchmove', onDrag);
				} else {
					offsetY = e.clientY;
					document.addEventListener('mouseup', stopDrag, { once: true });
					document.addEventListener('mousemove', onDrag);
				}
			},
			stopDrag = () => {
				document.removeEventListener('mousemove', onDrag);
				document.removeEventListener('touchmove', onDrag);
			},
			onDrag = (e) => {
				const clientY = e.type == 'touchmove' ? e.touches[0].clientY : e.clientY;
				consoleHeight = consoleOffsetHeight + offsetY - clientY;
			};
		resizer.addEventListener('mousedown', startDrag);
		resizer.addEventListener('touchstart', startDrag);
		return {
			destroy() {
				stopDrag();
				resizer.removeEventListener('mousedown', startDrag);
				resizer.removeEventListener('touchstart', startDrag);
			},
		};
	};
	const closeSocket = () => {
		if (debugSocket) {
			debugSocket.close();
		}
	};
	const sendScriptEval = async () => {
		if (!terminalValue || waitForEvalRequest) return;
		waitForEvalRequest = true;
		if (!running) {
			toast.open({
				type: 'danger',
				title: $format('script_needs_running', {
					default: 'Script needs to be running to evaluate this code',
				}),
			});
		} else if (terminalValue && (await scripsHelper.sendScriptEval(scriptID, terminalValue))) {
			terminalValue = '';
		}
		waitForEvalRequest = false;
	};
	const toggleSocket = () => {
		if (!loadingSocket) {
			wsRunning ? closeSocket() : startDebugLog();
		}
	};
	const handleKeydown = (e: KeyboardEvent) => {
		if (logEnabled && e.key == 'Enter') {
			sendScriptEval();
		}
	};

	onMount(() => {
		if (logEnabled) {
			startDebugLog();
			document.addEventListener('keydown', handleKeydown);
		}
	});
	onDestroy(() => {
		document.removeEventListener('keydown', handleKeydown);
		closeSocket();
	});
</script>

{#if logEnabled}
	<div class="console-container" style="height:{consoleHeight}px;">
		<div class="resize-wrapper" use:resizeWrapper>
			<span />
			<span />
		</div>
		<div class="console-menu">
			<Icon class="text-danger mb-xs" size="s" icon="fas fa-ban" on:click={() => (log = [])} />
			<Icon
				class="mb-xs {fullLog ? 'text-success' : 'text-color-3'}"
				size="s"
				icon="fas fa-bug"
				on:click={() => (fullLog = !fullLog)}
			/>
			<Icon
				class="mb-xs {!wsRunning ? 'text-success' : 'text-danger'}"
				size="s"
				icon="fas fa-{!wsRunning ? 'play' : 'stop'}"
				on:click={toggleSocket}
			/>
		</div>
		<div class="console" bind:this={consoleBox}>
			{#each log as item}
				{#if fullLog || item.level == -1}
					<div class="log-item">
						<div class="message text-{item.color}">{@html item.message}</div>
						<div class="time">{@html item.time}</div>
					</div>
				{/if}
			{/each}
		</div>
		<div class="terminal">
			<Icon size="s" icon="fas fa-terminal" class="text-color-3" />
			<input
				bind:value={terminalValue}
				on:focus={() => (placeholder = '')}
				on:blur={() => (placeholder = terminalPlaceholder)}
				{placeholder}
			/>
			<Icon
				size="s"
				icon="fas fa-turn-down-left"
				class="text-color-3"
				style="cursor:pointer;"
				on:click={sendScriptEval}
			/>
		</div>
	</div>
{/if}

<style>
	.console-container {
		width: 100%;
		min-height: 6px;
		max-height: calc(100% - 100px);
		margin-top: 1px;
		position: relative;
		flex-shrink: 0;
		z-index: 2;
	}

	.resize-wrapper {
		width: 100%;
		top: -3px;
		position: absolute;
		cursor: n-resize;
		display: flex;
		flex-direction: column;
		justify-content: center;
		align-items: center;
		user-select: none;
	}

	.resize-wrapper > span {
		width: 25px;
		outline: 4px solid white;
		border-top: 1px solid var(--color-1);
		position: relative;
		z-index: 1;
		padding: 2px 5px;
	}

	.console {
		width: 100%;
		height: calc(100% - 30px);
		overflow: hidden;
		overflow-y: auto;
		background: white;
		position: relative;
		padding: 5px 10px;
		font-size: 13px;
		line-height: 20px;
		display: flex;
		flex-direction: column;
		padding-left: 24px;
	}

	.console-menu {
		position: absolute;
		left: 2px;
		top: 4px;
		cursor: pointer;
		z-index: 2;
		display: flex;
		flex-direction: column;
	}

	.console .log-item {
		display: flex;
		justify-content: space-between;
		padding: 0 5px;
	}

	.console .log-item .time {
		color: var(--color-1);
		word-break: normal;
	}

	.terminal {
		display: flex;
		align-items: center;
		height: 30px;
		background: white;
		border-top: 1px solid var(--color-1);
		position: relative;
		z-index: 3;
		padding: 0 5px;
	}

	.terminal > input {
		width: calc(100% - 40px);
		font-size: 13px;
		line-height: 16px;
		border: none;
		outline: none;
		padding: 0 5px;
		color: var(--color-1);
		font-family: monospace;
	}

	.terminal > input::placeholder {
		font-family: monospace;
		font-style: normal;
		font-weight: normal;
		margin: 0;
		color: var(--color-1-opacity);
	}
</style>
